/**
 * Recent Files Panel Component
 *
 * Displays recently opened files with quick access.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFileStore } from '../index';
import type { RecentFile } from '../types';

interface RecentFilesPanelProps {
  onOpenFile?: () => void;
}

export function RecentFilesPanel({ onOpenFile }: RecentFilesPanelProps) {
  const { recentFiles, loadModel, clearRecentFiles, setSavedModelsDialogOpen } =
    useFileStore();
  const [error, setError] = useState<string | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  const handleOpenFile = useCallback(
    async (file: RecentFile) => {
      if (file.source === 'local') {
        try {
          setError(null);
          await loadModel(file.id);
          onOpenFile?.();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to open file');
          if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = setTimeout(() => { setError(null); }, 5000);
        }
      }
    },
    [loadModel, onOpenFile]
  );

  const handleClearRecent = useCallback(() => {
    if (confirm('Clear recent files history?')) {
      clearRecentFiles();
    }
  }, [clearRecentFiles]);

  const handleOpenAll = useCallback(() => {
    setSavedModelsDialogOpen(true);
  }, [setSavedModelsDialogOpen]);

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${String(minutes)}m ago`;
    if (hours < 24) return `${String(hours)}h ago`;
    if (days < 7) return `${String(days)}d ago`;
    if (days < 30) return `${String(Math.floor(days / 7))}w ago`;
    return date.toLocaleDateString();
  };

  if (recentFiles.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-semibold">Recent Files</h3>
        <button
          onClick={handleClearRecent}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-2 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <span>{error}</span>
          <button
            onClick={() => { setError(null); }}
            className="ml-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <div className="divide-y">
        {recentFiles.slice(0, 5).map((file) => (
          <button
            key={file.id}
            onClick={() => handleOpenFile(file)}
            disabled={file.source !== 'local'}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {/* Icon */}
            <div className="flex h-8 w-8 items-center justify-center rounded bg-muted">
              {file.thumbnail ? (
                <img
                  src={file.thumbnail}
                  alt=""
                  className="h-full w-full rounded object-cover"
                />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-muted-foreground"
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{file.name}</p>
              <p className="text-xs text-muted-foreground">
                {formatTimeAgo(new Date(file.lastOpened))}
                {file.source === 'imported' && ' (imported)'}
              </p>
            </div>
          </button>
        ))}
      </div>

      {recentFiles.length > 5 && (
        <div className="border-t px-4 py-2">
          <button
            onClick={handleOpenAll}
            className="text-sm text-primary hover:underline"
          >
            View all saved models
          </button>
        </div>
      )}
    </div>
  );
}
